import { Button, IconButton, Panel } from 'rsuite'
import { GetStaticProps, NextPage } from 'next'
import Link from 'next/link'
import ArrowLeftLineIcon from '@rsuite/icons/ArrowLeftLine'
import { FiExternalLink } from 'react-icons/fi'
import CenterLayout from '../../../components/shared/CenterLayout'
import CustomText from '../../../components/base/CustomText'
import {
  getAllGuidesByCategoryID,
  getSupportCategories,
  getSupportCategoryBySlug,
} from '../../../services/supportServices'
import { SupportCategory, SupportGuide } from '../../../ts/types/support.types'
import Placeholder from '../../../components/shared/Placeholder'

type Props = {
  category?: SupportCategory
  guides: SupportGuide[]
}

const GuidesPage: NextPage<Props> = ({ category, guides }) => {
  if (!category) {
    return (
      <CenterLayout maxWidth={900} showLogo>
        <Panel bodyFill shaded className="bg-white">
          <div className="px-8 pt-8 pb-4">
            <div className="border-b border-gray flex items-center gap-3 pb-4">
              <Link href="/support/guides" passHref>
                <IconButton as="a" icon={<ArrowLeftLineIcon />} circle size="md" className="bg-gray-lightest" />
              </Link>
              <CustomText size="heading-4" weight={700} className="text-gray-darker">
                Categoría no encontrada
              </CustomText>
            </div>

            <CustomText size="paragraph" weight={400} className="text-gray-darker my-5">
              Comprueba que la URL sea correcta o vuelve a la página de guías.
            </CustomText>
          </div>
        </Panel>
      </CenterLayout>
    )
  }

  return (
    <CenterLayout maxWidth={900} showLogo>
      <Panel bodyFill shaded className="bg-white">
        <div className="px-8 pt-8 pb-4">
          <div className="border-b border-gray flex items-center gap-3 pb-4">
            <Link href="/support/guides" passHref>
              <IconButton as="a" icon={<ArrowLeftLineIcon />} circle size="md" className="bg-gray-lightest" />
            </Link>
            <CustomText size="heading-4" weight={700} className="text-gray-darker">
              {category.name}
            </CustomText>
          </div>

          <CustomText size="paragraph" weight={400} className="text-gray-darker mt-5 mb-8">
            {category.description}
          </CustomText>

          {guides.length === 0 && (
            <div className="w-full bg-gray-lighter py-6 px-2 rounded-md">
              <Placeholder
                title="No hay guías disponibles"
                description="Actualmente no hay guías disponibles en esta categoría."
              />
            </div>
          )}

          <div className="divide-y divide-gray">
            {guides
              .filter((g) => g.link)
              .map((guide) => (
                <div key={category.id}>
                  <div className="-mx-4">
                    <Link href={guide.link ?? `/support/categories/${category.slug}`} passHref>
                      <Button as="a" target="_blank" rel="noopener noreferrer" appearance="subtle" size="lg" block>
                        <div className="flex items-center gap-8 text-left text-gray-darker">
                          <div className="flex-1 min-w-0">
                            <CustomText size="subtitle-2" weight={600} className="truncate">
                              {guide.title}
                            </CustomText>
                            <CustomText size="caption" weight={400} className="truncate">
                              {guide.description}
                            </CustomText>
                          </div>

                          <FiExternalLink className="text-gray-dark" />
                        </div>
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </Panel>
    </CenterLayout>
  )
}

GuidesPage.auth = false

// eslint-disable-next-line require-jsdoc
export const getStaticProps: GetStaticProps = async (context) => {
  const categorySlug = context.params?.categorySlug as string
  const category = await getSupportCategoryBySlug(categorySlug)
  const guides = await getAllGuidesByCategoryID(category.id)

  return {
    props: {
      category,
      guides,
    },
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 10 seconds
    revalidate: 60, // In seconds
  }
}

export const getStaticPaths = async () => {
  const categories = await getSupportCategories()

  return {
    paths: categories.map((category) => ({
      params: { categorySlug: category.slug },
    })),
    fallback: true,
  }
}

export default GuidesPage
